import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FaRandom, FaCopy, FaFont, FaCheck } from "react-icons/fa";

// Available Google Fonts
const availableFonts = [
  'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Roboto Condensed',
  'Source Sans Pro', 'Oswald', 'Raleway', 'PT Sans', 'Ubuntu',
  'Playfair Display', 'Merriweather', 'PT Serif', 'Lora', 'Bebas Neue',
  'Roboto Slab', 'Noto Sans', 'Nunito', 'Poppins', 'Titillium Web'
];

// Sample font pairs data
const fontPairs = [
  {
    id: 'montserrat-opensans',
    heading: {
      family: "'Montserrat', sans-serif",
      size: 28,
      weight: 700
    },
    body: {
      family: "'Open Sans', sans-serif",
      size: 16,
      weight: 400
    },
    name: 'Montserrat & Open Sans'
  },
  {
    id: 'playfair-lora',
    heading: {
      family: "'Playfair Display', serif",
      size: 32,
      weight: 700
    },
    body: {
      family: "'Lora', serif",
      size: 17,
      weight: 400
    },
    name: 'Playfair & Lora'
  },
  {
    id: 'roboto-roboto',
    heading: {
      family: "'Roboto Condensed', sans-serif",
      size: 30,
      weight: 700
    },
    body: {
      family: "'Roboto', sans-serif",
      size: 15,
      weight: 300
    },
    name: 'Roboto Condensed & Roboto'
  }
];

// Sample preview content
const previewContent = {
  Profile: {
    name: "Jane Doe",
    title: "UI/UX Lead at Cool Company",
    email: "jane.doe@coolcompany.com",
    phone: "+123 456 78 90",
    location: "New York, USA",
    biography: "I'm Jane Doe, UI/UX lead of Cool Company based in New York. I'm passionate about creating beautiful and functional user experiences.",
  },
  Article: {
    title: "The Art of Typography",
    subtitle: "How fonts shape user experience",
    paragraph1: "Typography is the art and technique of arranging type to make written language legible, readable, and appealing when displayed.",
  },
  Card: {
    title: "Featured Product",
    description: "Our premium design toolkit with carefully curated fonts and color palettes",
    price: "$49.99",
  }
};

export default function FontPairFinder() {
  const [currentPair, setCurrentPair] = useState(fontPairs[0]);
  const [previewType, setPreviewType] = useState("Profile");
  const [customSizes, setCustomSizes] = useState({
    heading: fontPairs[0].heading.size,
    body: fontPairs[0].body.size
  });
  const [customWeights, setCustomWeights] = useState({
    heading: fontPairs[0].heading.weight,
    body: fontPairs[0].body.weight
  });
  const [selectedFonts, setSelectedFonts] = useState({
    heading: fontPairs[0].heading.family,
    body: fontPairs[0].body.family
  });
  const [copied, setCopied] = useState(false);
  const { darkMode } = useTheme();

  // Load Google Fonts
  useEffect(() => {
    const loadFonts = () => {
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?${availableFonts
        .map(font => `family=${font.replace(/ /g, '+')}:wght@300;400;500;600;700;800;900`)
        .join('&')}&display=swap`;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    };

    loadFonts();
  }, []);

  // Update custom sizes/weights when current pair changes
  useEffect(() => {
    setCustomSizes({
      heading: currentPair.heading.size,
      body: currentPair.body.size
    });
    setCustomWeights({
      heading: currentPair.heading.weight,
      body: currentPair.body.weight
    });
    setSelectedFonts({
      heading: currentPair.heading.family,
      body: currentPair.body.family
    });
  }, [currentPair]);

  const handleFontChange = (type, font) => {
    const newFont = `'${font}', ${font.includes('Sans') || font.includes('Condensed') ? 'sans-serif' : 'serif'}`;
    
    setSelectedFonts(prev => ({
      ...prev,
      [type]: newFont
    }));

    setCurrentPair(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        family: newFont
      }
    }));
  };
  
  // Helper function to extract font name from font family string
  const getFontName = (fontFamily) => {
    if (!fontFamily) return '';
    // Remove quotes and everything after the first comma
    return fontFamily.replace(/'/g, '').split(',')[0].trim();
  };

  const handleSizeChange = (type, value) => {
    const newSize = parseInt(value) || 14;
    setCustomSizes(prev => ({
      ...prev,
      [type]: newSize
    }));
    
    setCurrentPair(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        size: newSize
      }
    }));
  };

  const handleWeightChange = (type, value) => {
    const newWeight = parseInt(value) || 400;
    setCustomWeights(prev => ({
      ...prev,
      [type]: newWeight
    }));
    
    setCurrentPair(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        weight: newWeight
      }
    }));
  };

  const getFontPair = () => {
    const randomPair = fontPairs[Math.floor(Math.random() * fontPairs.length)];
    setCurrentPair(randomPair);
  };

  const copyFontPair = () => {
    const css = `/* Font Pair */
.heading {
  font-family: ${currentPair.heading.family};
  font-size: ${currentPair.heading.size}px;
  font-weight: ${currentPair.heading.weight};
}

.body {
  font-family: ${currentPair.body.family};
  font-size: ${currentPair.body.size}px;
  font-weight: ${currentPair.body.weight};
}`;

    navigator.clipboard.writeText(css);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderPreviewContent = () => {
    const content = previewContent[previewType];
    
    switch(previewType) {
      case "Profile":
        return (
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" 
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <h1 style={{
                fontFamily: currentPair.heading.family,
                fontSize: `${customSizes.heading}px`,
                fontWeight: customWeights.heading,
                marginBottom: '0.5rem'
              }}>
                {content.name}
              </h1>
              <h2 style={{
                fontFamily: currentPair.heading.family,
                fontSize: `${customSizes.heading * 0.7}px`,
                fontWeight: customWeights.heading,
                marginBottom: '1rem',
                color: darkMode ? '#a0aec0' : '#4a5568'
              }}>
                {content.title}
              </h2>
              <div className="flex justify-center md:justify-start gap-4 mb-4">
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm rounded-full">
                  Web Designer
                </span>
                <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-sm rounded-full">
                  UI/UX Expert
                </span>
              </div>
              <p style={{
                fontFamily: currentPair.body.family,
                fontSize: `${customSizes.body}px`,
                fontWeight: customWeights.body,
                lineHeight: '1.6',
                marginBottom: '1.5rem',
                maxWidth: '600px'
              }}>
                {content.biography} I specialize in creating beautiful, user-friendly interfaces with a focus on accessibility and performance. With over 8 years of experience, I've helped numerous clients bring their digital products to life.
              </p>
              <div className="flex justify-center md:justify-start gap-4">
                <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition">
                  Contact Me
                </button>
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                  View Portfolio
                </button>
              </div>
            </div>
          </div>
        );
      
      case "Article":
        return (
          <div className="max-w-3xl mx-auto">
            <div className="mb-6 rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                alt="Article"
                className="w-full h-64 object-cover rounded-t-lg"
              />
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 inline-block rounded-br-lg">
                <span className="text-sm font-medium">FEATURED</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <span>By {content.author || 'Admin'}</span>
              <span>•</span>
              <span>{content.date || 'May 31, 2023'}</span>
              <span>•</span>
              <span>5 min read</span>
            </div>
            
            <h1 style={{
              fontFamily: currentPair.heading.family,
              fontSize: `${customSizes.heading * 1.2}px`,
              fontWeight: customWeights.heading,
              marginBottom: '1rem',
              lineHeight: '1.2'
            }}>
              {content.title}
            </h1>
            <h2 style={{
              fontFamily: currentPair.heading.family,
              fontSize: `${customSizes.heading * 0.7}px`,
              fontWeight: customWeights.heading,
              marginBottom: '2rem',
              color: darkMode ? '#a0aec0' : '#4a5568',
              lineHeight: '1.4'
            }}>
              {content.subtitle}
            </h2>
            
            <div className="space-y-4">
              <p style={{
                fontFamily: currentPair.body.family,
                fontSize: `${customSizes.body}px`,
                fontWeight: customWeights.body,
                lineHeight: '1.8',
                color: darkMode ? '#e2e8f0' : '#2d3748'
              }}>
                {content.paragraph1}
              </p>
              
              <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-6 italic text-lg text-gray-700 dark:text-gray-300">
                "Typography is the craft of endowing human language with a durable visual form."
              </blockquote>
              
              <p style={{
                fontFamily: currentPair.body.family,
                fontSize: `${customSizes.body}px`,
                fontWeight: customWeights.body,
                lineHeight: '1.8',
                color: darkMode ? '#e2e8f0' : '#2d3748'
              }}>
                The right font pairing can elevate your design and improve readability. It's not just about choosing beautiful fonts, but about creating harmony and hierarchy in your typography.
              </p>
              
              <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg my-6">
                <h3 className="font-bold text-lg mb-2" style={{
                  fontFamily: currentPair.heading.family,
                  fontWeight: customWeights.heading,
                }}>
                  Pro Tip
                </h3>
                <p style={{
                  fontFamily: currentPair.body.family,
                  fontSize: `${customSizes.body}px`,
                  lineHeight: '1.6'
                }}>
                  When pairing fonts, limit yourself to 2-3 typefaces. Use contrast in weight, size, and style to create visual hierarchy.
                </p>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img 
                    src="https://randomuser.me/api/portraits/women/44.jpg" 
                    alt="Author" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-medium" style={{ fontFamily: currentPair.heading.family }}>
                    {content.author || 'Sarah Johnson'}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Senior Designer & Typography Enthusiast
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "Card":
        return (
          <div className="flex justify-center">
            <div style={{
              maxWidth: '380px',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: darkMode ? '#2d3748' : '#ffffff',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              border: `1px solid ${darkMode ? '#4a5568' : '#e2e8f0'}`
            }} className="hover:shadow-xl hover:-translate-y-1">
              {/* Card Image */}
              <div className="relative h-48 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                  alt="Card"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                  POPULAR
                </div>
              </div>
              
              {/* Card Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                    Design
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {content.date || 'May 31, 2023'}
                  </span>
                </div>
                
                <h1 style={{
                  fontFamily: currentPair.heading.family,
                  fontSize: `${customSizes.heading * 0.9}px`,
                  fontWeight: customWeights.heading,
                  marginBottom: '0.75rem',
                  lineHeight: '1.3'
                }}>
                  {content.title}
                </h1>
                
                <p style={{
                  fontFamily: currentPair.body.family,
                  fontSize: `${customSizes.body}px`,
                  fontWeight: customWeights.body,
                  marginBottom: '1.5rem',
                  lineHeight: '1.6',
                  color: darkMode ? '#a0aec0' : '#4a5568'
                }}>
                  {content.description}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <img 
                        src="https://randomuser.me/api/portraits/men/32.jpg" 
                        alt="Author"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-sm font-medium" style={{ fontFamily: currentPair.body.family }}>
                      {content.author || 'Michael Chen'}
                    </span>
                  </div>
                  
                  <div className="text-right">
                    <div style={{
                      fontFamily: currentPair.heading.family,
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: darkMode ? '#63b3ed' : '#3182ce',
                      lineHeight: '1'
                    }}>
                      {content.price}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      per month
                    </span>
                  </div>
                </div>
                
                <button className="mt-6 w-full py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition duration-200 flex items-center justify-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Now
                </button>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <HeaderSection toolId="fontPairFinder" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Font Pair Finder</h1>
            <p className="opacity-90">Discover perfect font combinations for your next project</p>
          </div>

          {/* Controls */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={getFontPair}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center gap-2 transition"
              >
                <FaRandom /> Get New Pair
              </button>
              <button
                onClick={copyFontPair}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md flex items-center gap-2 transition"
              >
                {copied ? <FaCheck /> : <FaCopy />}
                {copied ? 'Copied!' : 'Copy CSS'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 dark:text-white">
              {/* Heading Font Controls */}
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                  <FaFont /> Heading Font
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Family
                    </label>
                    <select
                      value={getFontName(selectedFonts.heading)}
                      onChange={(e) => handleFontChange('heading', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                    >
                      {availableFonts.map(font => (
                        <option key={`heading-${font}`} value={font}>
                          {font}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Size: {customSizes.heading}px
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="72"
                      value={customSizes.heading}
                      onChange={(e) => handleSizeChange('heading', e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Weight: {customWeights.heading}
                    </label>
                    <select
                      value={customWeights.heading}
                      onChange={(e) => handleWeightChange('heading', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                    >
                      <option value="100">Thin (100)</option>
                      <option value="200">Extra Light (200)</option>
                      <option value="300">Light (300)</option>
                      <option value="400">Regular (400)</option>
                      <option value="500">Medium (500)</option>
                      <option value="600">Semi Bold (600)</option>
                      <option value="700">Bold (700)</option>
                      <option value="800">Extra Bold (800)</option>
                      <option value="900">Black (900)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Body Font Controls */}
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                  <FaFont /> Body Font
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Family
                    </label>
                    <select
                      value={getFontName(selectedFonts.body)}
                      onChange={(e) => handleFontChange('body', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                    >
                      {availableFonts.map(font => (
                        <option key={`body-${font}`} value={font}>
                          {font}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Size: {customSizes.body}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="24"
                      value={customSizes.body}
                      onChange={(e) => handleSizeChange('body', e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Weight: {customWeights.body}
                    </label>
                    <select
                      value={customWeights.body}
                      onChange={(e) => handleWeightChange('body', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                    >
                      <option value="100">Thin (100)</option>
                      <option value="200">Extra Light (200)</option>
                      <option value="300">Light (300)</option>
                      <option value="400">Regular (400)</option>
                      <option value="500">Medium (500)</option>
                      <option value="600">Semi Bold (600)</option>
                      <option value="700">Bold (700)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Type Selector */}
            <div className="mt-6 dark:text-white">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preview Type
              </label>
              <div className="flex flex-wrap gap-2">
                {['Profile', 'Article', 'Card'].map(type => (
                  <button
                    key={type}
                    onClick={() => setPreviewType(type)}
                    className={`px-4 py-2 rounded-md ${
                      previewType === type 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview Area */}
          <div className="p-6 dark:text-white">
            <div className="min-h-96 p-8 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
              {renderPreviewContent()}
            </div>
          </div>
        </div>
      </div>
      <BottomSection toolId="fontPairFinder" />
    </>
  );
}